﻿import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { apiCall, ENDPOINTS } from '../../config/api';
import { FiPlus, FiEdit2, FiTrash2, FiEye, FiCheck, FiX, FiSave, FiCopy, FiFilter } from 'react-icons/fi';
import { FaSignature, FaUser } from 'react-icons/fa';
import DocumentEditor from '../../components/DocumentEditor';
import Toast from '../../components/Toast';
import { useToast } from '../../hooks/useToast';

interface User {
	id: number;
	username: string;
	first_name: string;
	last_name: string;
	email: string;
}

interface Signature {
	id: string;
	name: string;
	file_path: string;
	file_name: string;
	file_size: number;
	sfdt_cache?: string; // SFDT format cached from DOCX
	html_cache?: string; // HTML cache for email footers
	html_preview?: string; // HTML preview thumbnail
	is_default: boolean;
	user?: number; // User ID
	user_name?: string;
	provider_name?: string;
	created_at: string;
	updated_at: string;
}

const Signatures: React.FC = () => {
	const { token, user } = useAuth();
	const navigate = useNavigate();
	const editorRef = useRef<any>(null);
	const { toast, showToast, hideToast } = useToast();
	
	const [signatures, setSignatures] = useState<Signature[]>([]);
	const [allSignatures, setAllSignatures] = useState<Signature[]>([]); // Unfiltered list
	const [loading, setLoading] = useState(true);
	const [editingSignature, setEditingSignature] = useState<Signature | null>(null);
	const [isEditorOpen, setIsEditorOpen] = useState(false);
	const [previewSignature, setPreviewSignature] = useState<Signature | null>(null);
	const [isPreviewOpen, setIsPreviewOpen] = useState(false);
	const [saving, setSaving] = useState(false);
	
	// Users and filtering
	const [users, setUsers] = useState<User[]>([]);
	const [selectedUserId, setSelectedUserId] = useState<string>('all');
	const [previewThumbnails, setPreviewThumbnails] = useState<Record<string, string>>({});
	
	// Form state
	const [formData, setFormData] = useState({
		name: '',
		content: '', // SFDT format
		html_preview: '',
		is_default: false,
		user_id: '' // User to assign signature to
	});

	useEffect(() => {
		fetchSignatures();
		fetchUsers();
	}, [token]);

	const fetchUsers = async () => {
		if (!token) return;
		
		try {
			const response = await apiCall(ENDPOINTS.USERS.LIST, {
				method: 'GET',
			}, token);

			if (response.ok) {
				const data = await response.json();
				setUsers(data.results || data || []);
			}
		} catch (error) {
			console.error('Error fetching users:', error);
		}
	};

	const fetchSignatures = async () => {
		if (!token) return;
		
		try {
			setLoading(true);
			const response = await apiCall(ENDPOINTS.SIGNATURES.LIST, {
				method: 'GET',
			}, token);

			if (response.ok) {
				const data = await response.json();
				const allSigs = data.results || data || [];
				setAllSignatures(allSigs);
				// Apply filter
				if (selectedUserId === 'all') {
					setSignatures(allSigs);
				} else {
					setSignatures(allSigs.filter(sig => sig.user?.toString() === selectedUserId));
				}
			} else {
				showToast('Failed to load signatures', 'error');
			}
		} catch (error) {
			console.error('Error fetching signatures:', error);
			showToast('Error loading signatures', 'error');
		} finally {
			setLoading(false);
		}
	};

	const handleAdd = () => {
		setFormData({
			name: '',
			content: '',
			html_preview: '',
			is_default: false,
			user_id: user?.id?.toString() || '' // Default to current user
		});
		setEditingSignature(null);
		setIsEditorOpen(true);
	};

	const handleEdit = async (signature: Signature) => {
		if (!token) return;
		
		try {
			// Fetch signature SFDT content from backend
			const response = await apiCall(`${ENDPOINTS.SIGNATURES.LIST}${signature.id}/content/`, {
				method: 'GET',
			}, token);
			
			if (response.ok) {
				const data = await response.json();
				setEditingSignature({
					...signature,
					sfdt_cache: data.content
				});
				setFormData({
					name: signature.name,
					content: data.content,
					html_preview: '',
					is_default: signature.is_default,
					user_id: signature.user?.toString() || ''
				});
				setIsEditorOpen(true);
			} else {
				showToast('Failed to load signature content', 'error');
			}
		} catch (error) {
			console.error('Error loading signature:', error);
			showToast('Error loading signature', 'error');
		}
	};

	const handleDelete = async (id: string) => {
		if (!token) return;
		if (!window.confirm('Are you sure you want to delete this signature?')) {
			return;
		}

		try {
			const response = await apiCall(`${ENDPOINTS.SIGNATURES.LIST}${id}/`, {
				method: 'DELETE',
			}, token);

			if (response.ok) {
				showToast('Signature deleted successfully', 'success');
				fetchSignatures();
			} else {
				showToast('Failed to delete signature', 'error');
			}
		} catch (error) {
			console.error('Error deleting signature:', error);
			showToast('Error deleting signature', 'error');
		}
	};

	const handlePreview = async (signature: Signature) => {
		if (!token) return;
		
		try {
			// Fetch signature content (SFDT) from backend
			const response = await apiCall(`${ENDPOINTS.SIGNATURES.LIST}${signature.id}/content/`, {
				method: 'GET',
			}, token);
			
			if (response.ok) {
				const data = await response.json();
				// Update signature with SFDT content for preview
				setPreviewSignature({
					...signature,
					sfdt_cache: data.content
				});
				setIsPreviewOpen(true);
			} else {
				showToast('Failed to load signature content', 'error');
			}
		} catch (error) {
			console.error('Error loading signature:', error);
			showToast('Error loading signature', 'error');
		}
	};

	const handleDuplicate = async (signature: Signature) => {
		if (!token) return;
		
		try {
			// Fetch signature content (SFDT) from backend
			const response = await apiCall(`${ENDPOINTS.SIGNATURES.LIST}${signature.id}/content/`, {
				method: 'GET',
			}, token);
			
			if (response.ok) {
				const data = await response.json();
				// Set editing signature with duplicated content
				setEditingSignature(null); // Not editing, creating new
				setFormData({
					name: `${signature.name} (Copy)`,
					content: data.content,
					html_preview: '',
					is_default: false, // Don't copy default status
					user_id: signature.user?.toString() || ''
				});
				setIsEditorOpen(true);
				showToast('Signature duplicated - modify and save', 'info');
			} else {
				showToast('Failed to duplicate signature', 'error');
			}
		} catch (error) {
			console.error('Error duplicating signature:', error);
			showToast('Error duplicating signature', 'error');
		}
	};

	const handleSetDefault = async (id: string) => {
		if (!token) return;

		try {
			const response = await apiCall(ENDPOINTS.SIGNATURES.SET_DEFAULT(id), {
				method: 'POST',
			}, token);

			if (response.ok) {
				showToast('Default signature updated', 'success');
				fetchSignatures();
			} else {
				showToast('Failed to set default signature', 'error');
			}
		} catch (error) {
			console.error('Error setting default:', error);
			showToast('Error setting default signature', 'error');
		}
	};

	const handleEditorChange = (sfdt: string) => {
		setFormData(prev => ({ ...prev, content: sfdt }));
	};

	const handleSave = async () => {
		console.log('[Signatures] handleSave CALLED');
		console.log('[Signatures] token:', !!token);
		console.log('[Signatures] formData.name:', formData.name);
		
		if (!token || !formData.name.trim()) {
			showToast('Please enter a signature name', 'warning');
			return;
		}

		if (!editorRef.current?.documentEditor) {
			showToast('Editor not initialized', 'error');
			return;
		}

		console.log('[Signatures] Setting saving state...');
		setSaving(true);
		
		try {
			console.log('[Signatures] Exporting to DOCX...');
			
			// Export to DOCX blob (same as documents)
			const blob = await new Promise<Blob>((resolve, reject) => {
				editorRef.current.documentEditor.saveAsBlob('Docx').then((blob: Blob) => {
					resolve(blob);
				}).catch((error: any) => {
					reject(error);
				});
			});
			
			console.log('[Signatures] DOCX blob size:', blob.size);
			
			// Get SFDT for caching
			const sfdtContent = editorRef.current.documentEditor.serialize();
			console.log('[Signatures] SFDT length:', sfdtContent.length);
			
			// Prepare FormData for file upload
			const uploadData = new FormData();
			uploadData.append('name', formData.name);
			uploadData.append('file', blob, `${formData.name}.docx`);
			uploadData.append('sfdt_content', sfdtContent);
			uploadData.append('is_default', formData.is_default.toString());
			if (formData.user_id) {
				uploadData.append('user_id', formData.user_id);
			}
			
			const url = editingSignature 
				? `${ENDPOINTS.SIGNATURES.LIST}${editingSignature.id}/`
				: ENDPOINTS.SIGNATURES.CREATE;
			
			const method = editingSignature ? 'PUT' : 'POST';

			console.log('[Signatures] API Call:', method, url);
			console.log('[Signatures] FormData fields:', {
				name: formData.name,
				file_size: blob.size,
				sfdt_length: sfdtContent.length,
				is_default: formData.is_default
			});

			console.log('[Signatures] Making API call...');
			const response = await apiCall(url, {
				method,
				body: uploadData,
			}, token);

			console.log('[Signatures] Response status:', response.status);
			console.log('[Signatures] Response ok:', response.ok);

			if (response.ok) {
				const data = await response.json();
				console.log('[Signatures] Save successful:', data);
				showToast(
					editingSignature ? 'Signature updated successfully' : 'Signature created successfully',
					'success'
				);
				setIsEditorOpen(false);
				fetchSignatures();
			} else {
				const error = await response.json();
				console.error('[Signatures] Save failed:', error);
				showToast(error.message || 'Failed to save signature', 'error');
			}
		} catch (error) {
			console.error('[Signatures] Error saving signature:', error);
			showToast('Error saving signature', 'error');
		} finally {
			console.log('[Signatures] Setting saving = false');
			setSaving(false);
		}
	};

	const handleCancel = () => {
		setIsEditorOpen(false);
		setEditingSignature(null);
		setFormData({
			name: '',
			content: '',
			html_preview: '',
			is_default: false,
			user_id: ''
		});
	};

	return (
		<div className="h-full flex flex-col bg-base-100">
			<Toast message={toast.message} type={toast.type} visible={toast.visible} onClose={hideToast} />
			
			{/* Header */}
			<div className="p-6 border-b border-base-300">
				<div className="flex justify-between items-start">
					<div>
						<h1 className="text-2xl font-bold text-base-content flex items-center gap-3">
							<FaSignature className="text-primary" />
							Signatures
						</h1>
					</div>
					<div className="flex items-center gap-3">
						{/* User Filter */}
						<div className="form-control">
							<div className="flex items-center gap-2 bg-base-200 rounded-lg px-3 py-2">
								<FiFilter className="w-4 h-4 opacity-60" />
								<select
									value={selectedUserId}
									onChange={(e) => setSelectedUserId(e.target.value)}
								className="select select-ghost focus:outline-none"
								>
									<option value="all">All Users</option>
									{users.map(user => (
										<option key={user.id} value={user.id.toString()}>
											{user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.username}
										</option>
									))}
								</select>
							</div>
						</div>
						<button
							onClick={handleAdd}
							className="btn btn-primary gap-2"
						>
							<FiPlus className="w-5 h-5" />
							New Signature
						</button>
					</div>
				</div>
			</div>

			{/* Signatures List */}
			<div className="flex-1 overflow-auto p-6">
				{loading ? (
					<div className="flex justify-center items-center h-64">
						<span className="loading loading-spinner loading-lg"></span>
					</div>
				) : signatures.length === 0 ? (
					<div className="text-center py-16">
						<FaSignature className="w-16 h-16 mx-auto opacity-20 mb-4" />
						<p className="text-lg opacity-60">No signatures found</p>
						<p className="text-sm opacity-40 mt-2">
							Click "New Signature" to create your first signature
						</p>
					</div>
				) : (
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
						{signatures.map((signature) => (
							<div 
								key={signature.id} 
								className="card bg-base-200 shadow-lg hover:shadow-xl transition-shadow"
							>
								<div className="card-body">
									<div className="flex justify-between items-start mb-2">
										<h3 className="card-title text-lg">{signature.name}</h3>
										{signature.is_default && (
											<span className="badge badge-success badge-sm gap-1">
												<FiCheck className="w-3 h-3" />
												Default
											</span>
										)}
									</div>
									
<div className="text-sm opacity-60 bg-base-100 p-3 rounded border border-base-300">
						<div className="flex items-center gap-2">
							<FaSignature className="w-4 h-4" />
							<span className="font-medium">{signature.file_name}</span>
						</div>
						<div className="text-xs opacity-50 mt-1">
							{(signature.file_size / 1024).toFixed(2)} KB • Click Preview to view
						</div>
					</div>
									
									<div className="card-actions justify-end mt-4 gap-2">
										<button
											onClick={() => handlePreview(signature)}
											className="btn btn-ghost btn-sm gap-1"
											title="Preview"
										>
											<FiEye className="w-4 h-4" />
											Preview
										</button>
										{!signature.is_default && (
											<button
												onClick={() => handleSetDefault(signature.id)}
												className="btn btn-ghost btn-sm gap-1"
												title="Set as Default"
											>
												<FiCheck className="w-4 h-4" />
												Set Default
											</button>
										)}
										<button
											onClick={() => handleEdit(signature)}
											className="btn btn-ghost btn-sm gap-1"
											title="Edit"
										>
											<FiEdit2 className="w-4 h-4" />
											Edit
										</button>
										<button
											onClick={() => handleDelete(signature.id)}
											className="btn btn-ghost btn-sm gap-1 text-error"
											title="Delete"
										>
											<FiTrash2 className="w-4 h-4" />
											Delete
										</button>
									</div>
								</div>
							</div>
						))}
					</div>
				)}
			</div>

			{/* Editor Modal */}
			{isEditorOpen && (
				<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
					<div className="bg-base-100 rounded-lg shadow-xl w-full max-w-6xl h-[90vh] flex flex-col">
						{/* Modal Header */}
						<div className="p-4 border-b border-base-300 flex items-center justify-between flex-shrink-0">
							<div className="flex-1">
								<h2 className="text-2xl font-semibold mb-2">
									{editingSignature ? 'Edit Signature' : 'Create Signature'}
								</h2>
								<div className="flex gap-2 max-w-2xl">
									<input
										type="text"
										value={formData.name}
										onChange={(e) => setFormData({...formData, name: e.target.value})}
										placeholder="Signature Name (e.g., Professional, Formal, Dr. Smith)"
										className="input input-bordered input-sm flex-1"
										required
									/>
									<select
										value={formData.user_id}
										onChange={(e) => setFormData({...formData, user_id: e.target.value})}
									className="select select-bordered w-48"
										required
									>
										<option value="" disabled>Assign to User...</option>
										{users.map(user => (
											<option key={user.id} value={user.id.toString()}>
												{user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.username}
											</option>
										))}
									</select>
								</div>
							</div>
							<div className="flex items-center gap-2">
								<label className="label cursor-pointer gap-2">
									<span className="label-text text-sm">Default</span>
									<input
										type="checkbox"
										checked={formData.is_default}
										onChange={(e) => setFormData({...formData, is_default: e.target.checked})}
										className="checkbox checkbox-primary checkbox-sm"
									/>
								</label>
								<button
									onClick={handleCancel}
									className="btn btn-ghost btn-sm btn-square"
									disabled={saving}
								>
									<FiX className="w-5 h-5" />
								</button>
							</div>
						</div>
						
						{/* Document Editor */}
						<div className="flex-1 overflow-hidden">
							<DocumentEditor
								ref={editorRef}
								height="100%"
								initialContent={editingSignature?.sfdt_cache}
								onChange={handleEditorChange}
								enableToolbar={true}
								showPropertiesPane={false}
							/>
						</div>

						{/* Modal Footer */}
						<div className="p-4 border-t border-base-300 flex justify-between items-center flex-shrink-0">
							<div className="text-sm opacity-60">
								Use the editor toolbar to format your signature with rich text, images, and tables
							</div>
							<div className="flex gap-2">
								<button
									type="button"
									onClick={handleCancel}
									className="btn btn-ghost"
									disabled={saving}
								>
									Cancel
								</button>
								<button
									onClick={() => {
										console.log('[Signatures] Save button clicked!');
										handleSave();
									}}
									className={`btn btn-primary gap-2 ${saving ? 'loading' : ''}`}
									disabled={saving}
								>
									{!saving && <FiSave className="w-4 h-4" />}
									{saving ? 'Saving...' : editingSignature ? 'Update' : 'Create'}
								</button>
							</div>
						</div>
					</div>
				</div>
			)}

			{/* Preview Modal */}
			{isPreviewOpen && previewSignature && (
				<div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
					<div className="bg-base-100 rounded-lg shadow-xl w-full max-w-4xl h-[80vh] flex flex-col">
						<div className="p-4 border-b border-base-300 flex items-center justify-between flex-shrink-0">
							<h2 className="text-xl font-semibold">{previewSignature.name}</h2>
							<button
								onClick={() => setIsPreviewOpen(false)}
								className="btn btn-ghost btn-sm btn-square"
							>
								<FiX className="w-5 h-5" />
							</button>
						</div>
						
						<div className="flex-1 overflow-hidden">
							{previewSignature.sfdt_cache ? (
								<DocumentEditor
									height="100%"
									initialContent={previewSignature.sfdt_cache}
									isReadOnly={true}
									enableToolbar={false}
									showPropertiesPane={false}
								/>
							) : (
								<div className="flex items-center justify-center h-full">
									<div className="text-center opacity-60">
										<p className="text-lg">No preview available</p>
										<p className="text-sm mt-2">Loading signature content...</p>
									</div>
								</div>
							)}
						</div>

						<div className="p-4 border-t border-base-300 flex justify-end flex-shrink-0">
							<button
								onClick={() => setIsPreviewOpen(false)}
								className="btn btn-ghost"
							>
								Close
							</button>
						</div>
					</div>
				</div>
			)}
		</div>
	);
};

export default Signatures;
